import React, { useState } from "react";
import { ChevronLeft, CheckCircle2, ShoppingBag } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import AddressForm from "./AddressForm";
import CartItem from "./CartItem";
import PaymentForm from "./PaymentForm";
import signupcheck from '../../images/payment_success.svg';

const Cart = ({ cartItems, updateCartPrice, updateProductSize, updateProductPharmacy, setCartOpen, deleteCartItem, clearCart }) => {
    const [nextScreen, setNextScreen] = useState('cart');
    const [productsData, setProductsData] = useState(null);
    const [cartError, setCartError] = useState(null);

    const handleNext = () => {
        const missingItems = cartItems
            ? cartItems.filter(item =>
                (item.Pharmacies.length > 0 && !item.selectedPharmacy) ||
                (item.Sizes.length > 0 && !item.selectedSize)
            ).map(item => item.ProductID)
            : [];

        if (missingItems.length > 0) {
            setCartError(missingItems);
            return;
        }

        setCartError(null);
        setNextScreen('addressinfo');
    };

    const handleSizeChange = (itemId, size) => {
        updateProductSize(itemId, size);
        setCartError(prevErrors => prevErrors?.filter(id => id !== itemId) || null);
    };

    const handlePharmacyChange = (itemId, pharmacy) => {
        updateProductPharmacy(itemId, pharmacy);
        setCartError(prevErrors => prevErrors?.filter(id => id !== itemId) || null);
    };

    const handleBack = () => {
        if (nextScreen === 'addressinfo') {
            setNextScreen('cart');
        } else if (nextScreen === 'payment') {
            setNextScreen('addressinfo');
        } else {
            setNextScreen('cart');
        }
    }

    const onSubmitAddressForm = (values) => {
        setProductsData(values)
        setNextScreen('payment')
    }

    const gotoAddressForm = () => {
        setNextScreen('addressinfo')
    }

    const handlePaymentSuccess = () => {
        setNextScreen('success')
        clearCart()
    }

    // Calculate total
    const calculateTotal = () => {
        return cartItems.reduce((total, item) => {
            return total + (parseFloat(item.selectedPrice?.QuantityPrice) || 0);
        }, 0).toFixed(2);
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="pb-4 space-y-3">
                {nextScreen !== 'cart' && nextScreen !== 'success' && (
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleBack}
                        className="mb-2"
                    >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                )}

                <h2 className="text-2xl font-semibold border-b border-border pb-3">Shopping Cart</h2>
                
                {nextScreen !== 'success' && (
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold">
                            {nextScreen === 'cart' ? 'My Products' : 
                             nextScreen === 'addressinfo' ? 'Shipping Information' : 
                             nextScreen === 'payment' ? 'Summary & Payment' : ''}
                        </h3>
                        {cartItems && cartItems.length > 0 && nextScreen === 'cart' && (
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={clearCart}
                            >
                                Clear Cart
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Content */}
            {cartItems && cartItems.length > 0 ? (
                <div className="flex flex-col flex-1">
                    {nextScreen === 'cart' ? (
                        <>
                            <div className="space-y-4 py-4">
                                {cartItems.map((item, index) => (
                                    <CartItem
                                        key={index}
                                        item={item}
                                        updateCartPrice={updateCartPrice}
                                        updateProductSize={handleSizeChange}
                                        updateProductPharmacy={handlePharmacyChange}
                                        deleteCartItem={deleteCartItem}
                                        isError={cartError?.includes(item.ProductID)}
                                    />
                                ))}
                            </div>

                            {cartError && cartError.length > 0 && (
                                <Alert variant="destructive" className="mt-4">
                                    <AlertDescription>
                                        Please select a pharmacy/size for the highlighted items.
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Cart Summary */}
                            <div className="mt-auto py-4 space-y-4 bg-background">
                                <Separator />
                                <div className="flex items-center justify-between text-lg font-semibold">
                                    <span>Total</span>
                                    <span>${calculateTotal()}</span>
                                </div>
                                <Button 
                                    className="w-full" 
                                    variant="secondary"
                                    size="lg"
                                    onClick={handleNext}
                                >
                                    Proceed to Checkout
                                </Button>
                            </div>
                        </>
                    ) : nextScreen === 'addressinfo' ? (
                        <AddressForm onSubmit={onSubmitAddressForm} gotoCart={() => setNextScreen('cart')} />
                    ) : nextScreen === 'payment' ? (
                        <PaymentForm 
                            productsData={productsData} 
                            cartItems={cartItems} 
                            paymentFormData={window.Square?.payments} 
                            gotoAddressForm={gotoAddressForm} 
                            handlePaymentSuccess={handlePaymentSuccess} 
                            gotoCart={() => setNextScreen('cart')} 
                        />
                    ) : null}
                </div>
            ) : nextScreen === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <div className="w-24 h-24 rounded-full bg-green-50 flex items-center justify-center">
                        <CheckCircle2 className="w-12 h-12 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-center">Thank you for your payment!</h3>
                    <p className="text-center text-muted-foreground max-w-sm">
                        You will receive an email with your receipt and shipping details
                    </p>
                    <Button 
                        onClick={() => setCartOpen(false)} 
                        className="mt-4"
                    >
                        Close
                    </Button>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-12 space-y-4 text-muted-foreground">
                    <ShoppingBag className="w-16 h-16 opacity-50" />
                    <p className="text-lg">Your cart is empty</p>
                    <Button variant="outline" onClick={() => setCartOpen(false)}>
                        Continue Shopping
                    </Button>
                </div>
            )}
        </div>
    );
};

export default Cart;

